import { FloatingToolbar as RemirrorFloatingToolbar, useEditorView } from '@remirror/react';
import { useGetAllToolbarOptions } from '../../hooks';
import { Divider, Space, Flex } from 'antd';
import { ACTIONS } from './../../config';
import React, { useEffect, useMemo } from 'react';
import './FloatingToolbar.css';
import {
  ImproveWritingButton,
  FixGrammerButton,
  TranslateButton,
  BoldButton,
  ItalicButton,
  UnderlineButton,
  CodeButton,
  LinkButton,
  TextColorButton,
  NodeConverterButton,
  InlineCommentButton,
  TextHighlightButton,
  ClearFormattingButton,
} from './../Buttons';

const ACTION_MAP = {
  [ACTIONS.NODE_CONVERTER]: { component: NodeConverterButton, show: (props) => props?.isEditable },
  [ACTIONS.AI_TRANSLATION]: { component: TranslateButton, show: (props) => props?.isEditable },
  [ACTIONS.AI_FIX_GRAMMER]: { component: FixGrammerButton, show: (props) => props?.isEditable },
  [ACTIONS.AI_IMPROVE_WRITING]: { component: ImproveWritingButton, show: (props) => props?.isEditable },
  [ACTIONS.BOLD]: { component: BoldButton, show: (props) => props?.isEditable },
  [ACTIONS.ITALIC]: { component: ItalicButton, show: (props) => props?.isEditable },
  [ACTIONS.UNDERLINE]: { component: UnderlineButton, show: (props) => props?.isEditable },
  [ACTIONS.LINK]: { component: LinkButton, show: (props) => props?.isEditable },
  [ACTIONS.CODE]: { component: CodeButton, show: (props) => props?.isEditable },
  [ACTIONS.TEXT_COLOR]: { component: TextColorButton, show: (props) => props?.isEditable },
  [ACTIONS.INLINE_COMMENT]: { component: InlineCommentButton, show: (props) => !props.isEditable },
  [ACTIONS.TEXT_HIGHLIGHT]: { component: TextHighlightButton, show: (props) => props?.isEditable },
  [ACTIONS.CLEAR_FORMATTING]: { component: ClearFormattingButton, show: (props) => props?.isEditable },
};

export const FloatingToolbar = ({ fileId, isEditable }) => {
  const floatingOptions = useGetAllToolbarOptions();
  const view = useEditorView();

  useEffect(() => {
    if (!view) return;

    const removeThreadMark = (event) => {
      const { detail } = event;
      if (!view || !detail?.threadId) return;

      const { state, dispatch } = view;
      const markType = state.schema.marks.inlineComment;
      let tr = state.tr;
      let markFound = false;

      try {
        state.doc.descendants((node, pos) => {
          if (node.isText && node.marks.length) {
            const mark = node.marks.find(
              (mark) => mark.type.name === 'inlineComment' && mark.attrs.threadId === detail.threadId
            );
            if (mark) {
              tr = tr.removeMark(pos, pos + node.text.length, markType);
              markFound = true;
            }
          }
        });

        if (markFound && dispatch && tr.docChanged) {
          dispatch(tr);
        }

        const ce = new CustomEvent('saveFileData', {
          detail: { external: true },
        });
        window.dispatchEvent(ce);
      } catch (err) {
        console.error('Unable to remove the thread mark:', err);
      }
    };

    window.addEventListener('thread-resolved', removeThreadMark);
    return () => {
      window.removeEventListener('thread-resolved', removeThreadMark);
    };
  }, [view]);

  const mainOptions = useMemo(
    () => floatingOptions?.filter((action) => action?.position === 'main'),
    [JSON.stringify(floatingOptions)]
  );

  return (
    <RemirrorFloatingToolbar className="onedoc_floating_toolbar">
      {mainOptions?.map((option) => {
        const Component = ACTION_MAP[option?.key]?.component;
        const show = ACTION_MAP[option?.key]?.show({ isEditable });
        const componentProps = { ...(ACTION_MAP[option?.key]?.props || {}), fileId };
        if (Component && show)
          return (
            <Flex key={option?.key} align="center">
              <Space className="toolbar__button__wrapper">
                <Component key={option?.key} {...componentProps} />
              </Space>
              {option?.showDivider && <Divider className="toolbar__divider" type="vertical" />}
            </Flex>
          );
        return null;
      })}
    </RemirrorFloatingToolbar>
  );
};

export default FloatingToolbar;
